import React, { useContext, useMemo, useState, useEffect } from "react";
import { AuthContext } from "../../context/auth";

import { guestTemplates, usherTemplates } from "../../helper/inviteTemplate";

import "./ShareLinkModal.css";

const ShareLinkModal = ({
  eventData,
  disabled,
  onClose,
  onCopy,
  visible,
  inviteRole = "guest",
}) => {
  const { user } = useContext(AuthContext);

  const [template, setTemplate] = useState("friendly");
  const [useCustomMessage, setUseCustomMessage] = useState(false);
  const [customMessage, setCustomMessage] = useState("");
  const [includeEventDate, setIncludeEventDate] = useState(true);
  const [includeHostName, setIncludeHostName] = useState(true);
  const [includeLocation, setIncludeLocation] = useState(true);
  const [copied, setCopied] = useState(false);

  const eventStatus = eventData?.approvalStatus || "pending";
  const canShare = eventStatus === "approved" && !disabled;

  useEffect(() => {
    if (visible) {
      setCopied(false);
    }
  }, [visible]);

  const effectiveEventData = useMemo(() => {
    return {
      inviteCode: eventData?.inviteCode || "",
      usherCode: eventData?.usherCode || "",
      title: eventData?.eventTitle || "",
      date: eventData?.eventDate || "",
      time: eventData?.eventTime || "",
      location:
        eventData?.eventLandmark ||
        eventData?.customAddress ||
        eventData?.eventLocation ||
        "",
    };
  }, [eventData]);

  const parseDate = (str) => {
    if (!str) return null;
    return new Date(str);
  };

  const escapeRegex = (text = "") =>
  text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const formatPreviewMessage = (message) => {
  const title = effectiveEventData.title;
  const escapedTitle = title ? escapeRegex(title) : "";

  const pattern = new RegExp(
    `(Invite Code:\\s*\\w+|Usher Code:\\s*\\w+${escapedTitle ? `|${escapedTitle}` : ""})`,
    "g"
  );

  return message.replace(pattern, "<strong>$1</strong>");
};


  const formatDate = () => {
    if (!effectiveEventData.date) return "";
    const d = parseDate(effectiveEventData.date);
    return d?.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatTime = () => {
    if (!effectiveEventData.time) return "";
    const parsed = new Date(`1970-01-01T${effectiveEventData.time}`);
    if (isNaN(parsed)) return effectiveEventData.time;
    return parsed.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const buildInviteLink = () => {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const hostId = user?._id;
    if (!hostId) return "";

    const isUsher = inviteRole === "usher";
    const code = isUsher
      ? effectiveEventData.usherCode
      : effectiveEventData.inviteCode;

    if (!code) return "";

    const path = isUsher ? "joinUsher" : "joinEvent";
    const queryParam = inviteRole === "usher" ? "usherCode" : "invitecode";
    return `${baseUrl}/${path}/${hostId}?${queryParam}=${code}`;
  };

  const getInviteDetails = () => {
    const link = buildInviteLink();
    const code =
      inviteRole === "usher"
        ? effectiveEventData.usherCode
        : effectiveEventData.inviteCode;

    const context = {
      title: effectiveEventData.title,
      date: includeEventDate ? formatDate() : "",
      time: includeEventDate ? formatTime() : "",
      location: includeLocation ? effectiveEventData.location : "",
      host: includeHostName ? `${user?.firstName} ${user?.lastName}` : "",
      link,
      userCode: code,
    };

    const templates = inviteRole === "usher" ? usherTemplates : guestTemplates;

    let message = useCustomMessage
      ? customMessage
      : templates[template](context);

    Object.entries(context).forEach(([key, val]) => {
      message = message.replaceAll(`{${key}}`, val || "");
    });

    return { link, message };
  };

  const handleCopy = async () => {
    const { message } = getInviteDetails();
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch(err) {
      alert("Failed to copy message");
    }
  };

  const handleShare = async () => {
    const { message } = getInviteDetails();

    if (navigator.share) {
      try {
        await navigator.share({
          title: effectiveEventData.title,
          text: message, 
        });
      } catch (e) {}
    } else {
      alert("Sharing is not supported in this browser.");
    }
  };

  if (!visible) return null;

  return (
    <div className="shareModalOverlay">
      <div className="shareModal">
        <div className="modalHeaderLine"></div>

        <div className="shareHeader">
          <h2>Share Invitation</h2>
          <p className="shareSubtitle">
            Customize your invitation for "
            {inviteRole === "usher"
              ? "Usher onboarding link"
              : "Guest invitation"}
            "
          </p>

          <button className="closeBtn" onClick={onClose}>
            ✖
          </button>
        </div>

        <div className="section">
          <h3 className="sectionTitle">Message Style</h3>

          <div className="templateTabs">
            {["friendly", "formal", "casual"].map((t) => (
              <button
                key={t}
                className={`tabBtn ${template === t ? "active" : ""}`}
                onClick={() => setTemplate(t)}
                // disabled={!canShare}
              >
                {t.charAt(0).toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div className="section">
          <h3 className="sectionTitle">Customize Details</h3>

          <div className="toggleRow">
            <span>Include Event Date/Time</span>
            <label className="switch">
              <input
                type="checkbox"
                checked={includeEventDate}
                onChange={(e) => setIncludeEventDate(e.target.checked)}
                // disabled={!canShare}
              />
              <span className="slider round"></span>
            </label>
          </div>

          <div className="toggleRow">
            <span>Include Host Name</span>
            <label className="switch">
              <input
                type="checkbox"
                checked={includeHostName}
                onChange={(e) => setIncludeHostName(e.target.checked)}
                // disabled={!canShare}
              />
              <span className="slider round"></span>
            </label>
          </div>

          <div className="toggleRow">
            <span>Include Location</span>
            <label className="switch">
              <input
                type="checkbox"
                checked={includeLocation}
                onChange={(e) => setIncludeLocation(e.target.checked)}
                // disabled={!canShare}
              />
              <span className="slider round"></span>
            </label>
          </div>

          <div className="toggleRow">
            <span>Use Custom Message</span>
            <label className="switch">
              <input
                type="checkbox"
                checked={useCustomMessage}
                onChange={(e) => setUseCustomMessage(e.target.checked)}
                // disabled={!canShare}
              />
              <span className="slider round"></span>
            </label>
          </div>
        </div>

        {useCustomMessage && (
          <div className="section">
            <h3 className="sectionTitle">Custom Message</h3>
            <div className="placeholderButtons">
              {[
                "{title}",
                "{date}",
                "{time}",
                "{location}",
                "{host}",
                "{userCode}",
                "{link}",
              ].map((tag) => (
                <button
                  key={tag}
                  className="tagBtn"
                  onClick={() => setCustomMessage((msg) => msg + tag)}
                >
                  {tag}
                </button>
              ))}
            </div>

            <textarea
              className="customTextarea"
              rows="5"
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
            />
          </div>
        )}

        <div className="section">
          <div className="previewHeader">
            <h3 className="sectionTitle">Preview</h3>
            <button className="copyBtn" onClick={handleCopy}>
              {copied ? "Copied!" : "Copy"}
            </button>
          </div>

          <div className="previewCard">
            <div
              className="inviteMessage"
              dangerouslySetInnerHTML={{
                __html: formatPreviewMessage(getInviteDetails().message),
              }}
            />
          </div>
        </div>

        <button className="shareBtn" onClick={handleShare}>
          Share Invitation
        </button>
      </div>
    </div>
  );
};

export default ShareLinkModal;
